import { z } from 'zod';
import { ACCEPTANCE_CHANNELS, CONTENT_PAGE_KINDS } from '@bulk/types';
import { idSchema, patchOf, slugSchema } from './common';

const localizedText = z.record(z.string(), z.string());

export const contentPageSchema = z.object({
  id: idSchema,
  gymId: idSchema,
  slug: z.string(),
  kind: z.enum(CONTENT_PAGE_KINDS),
  title: localizedText,
  body: localizedText,
  publishedTitle: localizedText.nullable(),
  publishedBody: localizedText.nullable(),
  version: z.number().int(),
  requiresAcceptance: z.boolean(),
  showInFooter: z.boolean(),
  sortOrder: z.number().int(),
  draftUpdatedAt: z.string(),
  publishedAt: z.string().nullable(),
  hasUnpublishedChanges: z.boolean(),
  acceptanceCount: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ContentPageDto = z.infer<typeof contentPageSchema>;

export const createContentPageSchema = z.object({
  slug: slugSchema,
  kind: z.enum(CONTENT_PAGE_KINDS).default('PAGE'),
  title: localizedText,
  body: localizedText.default({}),
  requiresAcceptance: z.boolean().default(false),
  showInFooter: z.boolean().default(true),
  sortOrder: z.number().int().min(0).max(1000).default(0),
});
export type CreateContentPageInput = z.infer<typeof createContentPageSchema>;

export const updateContentPageSchema = patchOf(createContentPageSchema);
export type UpdateContentPageInput = z.infer<typeof updateContentPageSchema>;

export const contentPageListQuerySchema = z.object({
  kind: z.enum(CONTENT_PAGE_KINDS).optional(),
});

export const publicContentPageSummarySchema = z.object({
  slug: z.string(),
  kind: z.enum(CONTENT_PAGE_KINDS),
  title: localizedText,
  requiresAcceptance: z.boolean(),
  showInFooter: z.boolean(),
  version: z.number().int(),
  publishedAt: z.string(),
});
export type PublicContentPageSummaryDto = z.infer<typeof publicContentPageSummarySchema>;

export const publicContentPageSchema = publicContentPageSummarySchema.extend({
  body: localizedText,
});
export type PublicContentPageDto = z.infer<typeof publicContentPageSchema>;

export const acceptanceRequirementSchema = z.object({
  slug: z.string(),
  title: localizedText,
  version: z.number().int(),
});
export type AcceptanceRequirement = z.infer<typeof acceptanceRequirementSchema>;

export const acceptContentPageSchema = z.object({
  version: z.number().int().min(1),
});
export type AcceptContentPageInput = z.infer<typeof acceptContentPageSchema>;

export const contentAcceptanceSchema = z.object({
  id: idSchema,
  pageId: idSchema,
  slug: z.string(),
  memberId: idSchema,
  version: z.number().int(),
  acceptedVia: z.enum(ACCEPTANCE_CHANNELS),
  acceptedAt: z.string(),
});
export type ContentAcceptanceDto = z.infer<typeof contentAcceptanceSchema>;

export const memberDocumentSchema = publicContentPageSummarySchema.extend({
  acceptedVersion: z.number().int().nullable(),
  accepted: z.boolean(),
});
export type MemberDocumentDto = z.infer<typeof memberDocumentSchema>;
